/**
 * TriagePills — status pills on the Loan Arrears Ageing rows / Customer File header (Dean, 30 Oct 2026).
 * Same visual language as the Accounts Inbox triage badges: short text on the pill, the full name + reason on hover.
 */
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Sparkles } from "lucide-react";

export const PILL_TONES = {
  rose:    "bg-rose-50 text-rose-800 border-rose-300",
  red:     "bg-red-600 text-white border-red-700",
  orange:  "bg-orange-50 text-orange-900 border-orange-300",
  amber:   "bg-amber-50 text-amber-900 border-amber-300",
  sky:     "bg-sky-50 text-sky-800 border-sky-300",
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-300",
  teal:    "bg-teal-50 text-teal-800 border-teal-300",
  indigo:  "bg-indigo-50 text-indigo-800 border-indigo-300",
  blue:    "bg-red-600 text-white border-red-700",          // R — keeps the red "R" badge the team already knows
  green:   "bg-green-50 text-green-800 border-green-300",
  slate:   "bg-slate-100 text-slate-700 border-slate-300",
};

export const PILL_META = [
  { key: "disputed",        label: "DISPUTED",        tone: "rose" },
  { key: "pdn_expired",     label: "PDN EXPIRED",     tone: "red" },
  { key: "broken_promise",  label: "BROKEN PROMISE",  tone: "orange" },
  { key: "missing_payment", label: "MISSING PAYMENT", tone: "amber" },
  { key: "promise_to_pay",  label: "PROMISE TO PAY",  tone: "sky" },
  { key: "paid",            label: "PAID",            tone: "emerald" },
  { key: "partial_payment", label: "PARTIAL PAYMENT", tone: "teal" },
  { key: "new_in_arrears",  label: "NEW IN ARREARS",  tone: "indigo" },
  { key: "restructured",    label: "RESTRUCTURED",    tone: "blue" },
  { key: "responsive",      label: "RESPONSIVE",      tone: "green" },
  { key: "unresponsive",    label: "UNRESPONSIVE",    tone: "slate" },
  { key: "cancelled_mandate", label: "CANCELLED MANDATE", tone: "red", regions: ["AUS"] },
];
// Pills that apply on the desk you're looking at (CANCELLED MANDATE is GoCardless AU only)
export const pillsForRegion = (region) => PILL_META.filter((p) => !p.regions || p.regions.includes(region));

const fmtAt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

export function TriagePill({ pill, caseId, size = "sm" }) {
  const tone = PILL_TONES[pill.tone] || PILL_TONES.slate;
  const cls = size === "md" ? "text-[10px] h-5 px-2" : "text-[9px] h-4 px-1.5";
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className={`${cls} font-bold tracking-wide whitespace-nowrap cursor-help ${tone}`} data-testid={`triage-pill-${pill.key}-${caseId}`}>
            {pill.source === "ai" && <Sparkles className="w-2.5 h-2.5 mr-0.5 opacity-80" />}{pill.short || pill.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start" className="text-xs max-w-xs">
          <div className="font-semibold">{pill.label}{pill.confidence ? ` · ${pill.confidence}%` : ""}</div>
          {pill.reason && <div className="mt-0.5 text-slate-200">{pill.reason}</div>}
          {pill.source === "ai" && <div className="mt-1 text-[10px] opacity-70">Read from recent emails / notes by AI</div>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Fallback before the first triage run — only what the row itself proves (a /2-style loan number = restructured).
const fallbackPills = (row) => (/\/\d+$/.test((row?.loan_application_id || "").trim())
  ? [{ key: "restructured", label: "RESTRUCTURED", short: "R", tone: "blue", reason: `Restructured loan — loan number ${row.loan_application_id}` }] : []);

export const rowPills = (row) => (row?.triage?.pills?.length ? row.triage.pills : fallbackPills(row));
export const rowPillKeys = (row) => rowPills(row).map((p) => p.key);

export function TriagePills({ row, size = "sm", className = "" }) {
  const pills = rowPills(row);
  if (!pills.length) return null;
  return (
    <span className={`inline-flex flex-wrap items-center gap-1 ${className}`} data-testid={`triage-pills-${row.case_id}`}
      title={row?.triage?.at ? `AI triage ${fmtAt(row.triage.at)}` : undefined}>
      {pills.map((p) => <TriagePill key={p.key} pill={p} caseId={row.case_id} size={size} />)}
    </span>
  );
}
